import { Button } from '@web/components/ui/button';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from '@web/components/ui/form';
import { UseFormReturn } from 'react-hook-form';

import { Spinner } from '@web/components/ui/spinner';
import { CollectPaymentForm as CollectPaymentFormType } from '@shared/types/collect-payment-form';
import { useState } from 'react';

import { Label } from '@web/components/ui/label';
import { Separator } from '@web/components/ui/separator';

import { CardDetails } from '@web/components/ui/card-details';
import { InputAnimatedLabel } from '@web/components/ui/input-animated-label';
import { InputMoney } from '@web/components/ui/input-money';
import { baseUnitQuantityToAmountString } from '@shared/utils';

export function CollectPublicPaymentForm({
  form,
  onSubmit,
}: {
  form: UseFormReturn<CollectPaymentFormType>;
  onSubmit?: (data: CollectPaymentFormType) => Promise<void>;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const paymentAmountIsEditable = form.getValues('payment.isEditable');

  const handleSubmit = async (data: CollectPaymentFormType) => {
    setIsSubmitting(true);
    if (!paymentAmountIsEditable) {
      form.setValue('payment.amount', data.payment.amount);
    }
    await onSubmit?.(data);

    setIsSubmitting(false);
  };

  const handleCardDetailsChange = ({
    number,
    exp,
    cvv,
  }: {
    number?: string;
    cvv?: string;
    exp?: string;
  }) => {
    if (number) form.setValue('card.number', number);
    if (exp) form.setValue('card.exp', exp);
    if (cvv) form.setValue('card.cvv', cvv);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          void form.handleSubmit(handleSubmit)(e);
        }}
        className="mt-6 grid grid-cols-6 gap-2 gap-y-6"
      >
        <Label className="col-span-full font-bold">Card Details</Label>

        <CardDetails
          className="col-span-6"
          onChange={handleCardDetailsChange}
        />

        <Separator className="col-span-6 my-4" />

        <Label className="col-span-full font-bold">Billing Information</Label>

        <FormField
          control={form.control}
          name="billing.firstName"
          render={({ field }) => (
            <FormItem className="col-span-3">
              <InputAnimatedLabel label="First Name" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.lastName"
          render={({ field }) => (
            <FormItem className="col-span-3">
              <InputAnimatedLabel label="Last Name" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.address1"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <InputAnimatedLabel label="Address" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.zip"
          render={({ field }) => (
            <FormItem className="col-span-2">
              <InputAnimatedLabel label="ZIP" {...field} />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="billing.email"
          render={({ field }) => (
            <FormItem className="col-span-4">
              <InputAnimatedLabel label="Email" {...field} />
            </FormItem>
          )}
        />

        <Separator className="col-span-6 my-4" />
        {paymentAmountIsEditable ? (
          <>
            <Label className="col-span-full font-bold">
              <span>Payment</span>
            </Label>

            <FormField
              control={form.control}
              name="payment.amount"
              render={({ field }) => (
                <FormItem className="col-span-2">
                  <FormControl>
                    <InputMoney value={field.value} onChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </>
        ) : (
          <div className="col-span-full flex items-center justify-between">
            <h5 className="text-left font-medium text-gray-700">Total</h5>
            <h2 className="mr-4 text-right text-xl font-semibold text-gray-900">
              $
              {baseUnitQuantityToAmountString({
                baseUnitQuantity: form.getValues('payment.amount'),
              })}
            </h2>
          </div>
        )}

        <Button
          type="submit"
          disabled={isSubmitting}
          className="col-span-full mt-6"
        >
          {isSubmitting ? <Spinner /> : 'Submit'}
        </Button>
      </form>
    </Form>
  );
}
